import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { setFilterParams } from '../../../helpers/setFilterParams'
import { updateURL } from '../../../helpers/updateUrl'
import store from '../../../store'
import {
  FetchLayoutsRequest,
  setAllFilters,
  SetLayoutsFilter,
  SetLayoutsItems,
} from '../../../store/layouts/actions'
import { getLayouts } from '../../../store/layouts/layouts'
import {
  FilterRooms,
  LayoutsFilterType,
  LayoutsStateType,
} from '../../../store/layouts/types'
import { getProject } from '../../../store/project/project'
import { CheckBoxButton } from '../../UI/CheckBoxButton/CheckBoxButton'
import { FiltersDropdown } from '../../UI/FiltersDropdown/FiltersDropdown'
import InputRange from '../../UI/InputRange/InputRange'
import style from './ApartmentFilter.module.scss'

const ApartmentFilter = ({ id }: { id: string }) => {
  const data: LayoutsStateType = useSelector(getLayouts)
  const projectData = useSelector(getProject)
  const [sortValue, setSortValue] = useState<string>('')
  const [initFilterValues, setInitFilterValues] = useState<boolean>(false)
  const [activeBtnMore3, setActiveBtnMore3] = useState<boolean>(false)

  const setFiltersFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const filter: any = { ...data.filters }

    for (const key in filter) {
      if (typeof filter[key] === 'object' && filter[key]) {
        const rooms = urlParams.get(key)?.split('-or-')

        rooms?.forEach(room => {
          filter.rooms[room as keyof FilterRooms] = true

          if (Number(room) > 2) setActiveBtnMore3(true)
        })
      } else {
        filter[key] = urlParams.get(key)
      }
    }

    store.dispatch(setAllFilters(filter as LayoutsFilterType))
    setInitFilterValues(true)
  }

  const sortItems = () => {
    if (data.layouts) {
      const sortedArray = [...data.layouts?.project.items]

      if (sortValue.length !== 0) {
        sortedArray.sort((a: any, b: any) => {
          const value = sortValue.split(',')[0]

          if (Number(a[value]) < Number(b[value])) {
            return -1
          }
          if (a[value] > b[value]) {
            return 1
          }
          return 0
        })

        if (sortValue.split(',')[1] === '<') sortedArray.reverse()
      }

      store.dispatch(SetLayoutsItems(sortedArray))
    }
  }

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const name = e.target.name
    let value: any = e.target.checked ? e.target.value : null

    if (name === 'rooms') {
      value = e.target.checked
        ? { ...data.filters.rooms, [e.target.value]: true }
        : { ...data.filters.rooms, [e.target.value]: null }

      if (e.target.value === 'more' && !e.target.checked) {
        for (const key in value) {
          const numberKey = Number(key)

          if (numberKey > 2) {
            value[key] = null
          }
        }
      }
    }

    store.dispatch(SetLayoutsFilter({ name, value }))
  }

  const handleRange = (value: number | number[], name?: string) => {
    if (Array.isArray(value)) {
      store.dispatch(SetLayoutsFilter({ name: `${name}from`, value: value[0] }))
      store.dispatch(SetLayoutsFilter({ name: `${name}to`, value: value[1] }))
    }
  }

  const handleSort = (value: string) => {
    setSortValue(value)
  }

  // Получение данных при изменении фильтра
  useEffect(() => {
    if (initFilterValues) {
      store.dispatch(FetchLayoutsRequest({ id }))
      updateURL(setFilterParams())
    }
  }, [data.filters])

  // Получение данных при первом заходе на страницу
  useEffect(() => {
    setFiltersFromUrl()
    store.dispatch(FetchLayoutsRequest({ id }))
  }, [])

  useEffect(() => {
    sortItems()
  }, [data.filters, data.layouts, sortValue])

  return (
    <div className={style.ApartmentFilter}>
      <div className={style.ApartmentFilter__row}>
        <div className={style.ApartmentFilter__block}>
          <div className={style.ApartmentFilter__checkbox}>
            <CheckBoxButton
              label={'Студия'}
              value={'studio'}
              width='112px'
              borderRadius='32px'
              checked={data.filters.rooms?.studio ? true : false}
              onChange={handleCheckbox}
              name={'rooms'}
            />
          </div>
          <div className={style.ApartmentFilter__checkbox}>
            <CheckBoxButton
              label={'1'}
              value={'1'}
              width='48px'
              borderRadius='100%'
              onChange={handleCheckbox}
              name={'rooms'}
              checked={data.filters.rooms?.[1] ? true : false}
            />
          </div>
          <div className={style.ApartmentFilter__checkbox}>
            <CheckBoxButton
              label={'2'}
              value={'2'}
              width='48px'
              borderRadius='100%'
              onChange={handleCheckbox}
              name={'rooms'}
              checked={data.filters.rooms?.[2] ? true : false}
            />
          </div>
          <div className={style.ApartmentFilter__checkbox}>
            <CheckBoxButton
              label={'3+'}
              value={'more'}
              name={'rooms'}
              width='48px'
              borderRadius='100%'
              onChange={handleCheckbox}
              checked={
                data.filters.rooms?.more || activeBtnMore3 ? true : false
              }
            />
          </div>
        </div>
        {projectData.project !== null && (
          <div className={style.ApartmentFilter__block}>
            <InputRange
              name='price-'
              rangeValue={[
                projectData.project?.prices.from,
                projectData.project?.prices.to,
              ]}
              onChange={handleRange}
              startValues={
                data.filters['price-from'] && data.filters['price-to']
                  ? [
                      Number(data.filters['price-from']),
                      Number(data.filters['price-to']),
                    ]
                  : null
              }
              initialValue={initFilterValues}
            />
          </div>
        )}
      </div>
      <div className={style.ApartmentFilter__row}>
        {projectData.project && (
          <div className={style.ApartmentFilter__block}>
            <div className={style.ApartmentFilter__label}>Площадь</div>
            <InputRange
              step={1}
              name='area_'
              rangeValue={[
                projectData.project.areas.from,
                projectData.project.areas.to,
              ]}
              startValues={
                data.filters.area_from && data.filters.area_to
                  ? [
                      Number(data.filters.area_from),
                      Number(data.filters.area_to),
                    ]
                  : null
              }
              onChange={handleRange}
              classModifiers={['width300']}
              initialValue={initFilterValues}
            />
          </div>
        )}
        <div
          className={`
            ${style.ApartmentFilter__block}
            ${style['ApartmentFilter__block--stages']}
          `}
        >
          <div className={style.ApartmentFilter__label}>Этаж</div>
          <InputRange
            rangeValue={
              projectData.project
                ? [
                    projectData.project.stages.from,
                    projectData.project.stages.to,
                  ]
                : ['0', '0']
            }
            startValues={
              data.filters.stage_from && data.filters.stage_to
                ? [
                    Number(data.filters.stage_from),
                    Number(data.filters.stage_to),
                  ]
                : null
            }
            name='stage_'
            classModifiers={['width155']}
            slider={false}
            onChange={handleRange}
            initialValue={initFilterValues}
          />
        </div>
        <div className={style.ApartmentFilter__block}>
          <div className={style.ApartmentFilter__checkbox}>
            <CheckBoxButton
              label={'Не первый'}
              value={'2'}
              width='137px'
              borderRadius='32px'
              name='stage_from'
              onChange={handleCheckbox}
              checked={data.filters.notFirstStage ? true : false}
            />
          </div>
          <div className={style.ApartmentFilter__checkbox}>
            <CheckBoxButton
              label={'Не последний'}
              value={
                projectData.project
                  ? (Number(projectData.project.stages.to) - 1).toString()
                  : '0'
              }
              width='160px'
              borderRadius='32px'
              name='stage_to'
              onChange={handleCheckbox}
              checked={data.filters.notLastStage ? true : false}
            />
          </div>
        </div>
        <div className={style.ApartmentFilter__block}>
          <FiltersDropdown
            options={[
              {
                value: 'UF_PRICE,>',
                label: 'Сначала дешевые',
              },
              {
                value: 'UF_AREA,<',
                label: 'Большая площадь сначала',
              },
              {
                value: 'UF_AREA,>',
                label: 'Меньшая площадь сначала',
              },
            ]}
            onSelect={handleSort}
            btnText='Сортировать'
            selected={''}
          />
        </div>
      </div>
    </div>
  )
}

export default ApartmentFilter
